import { Controller } from "@hotwired/stimulus"
import Sortable from 'sortablejs/modular/sortable.core.esm.js'
import lodash from "lodash"

export default class extends Controller {
  static targets = ["search", "results", "selected", "input", "emptyState", "counter"]
  static values = {
    searchUrl: String,
    sortUrl: String,
    maxPlaylists: { type: Number, default: 10 }
  }

  connect() {
    this.clickOutsideHandler = this.clickOutside.bind(this)
    this.initSortable()
    this.updateEmptyState()
    this.updateCounter()
    window.addEventListener("click", this.clickOutsideHandler)
  }

  disconnect() {
    window.removeEventListener("click", this.clickOutsideHandler)
    this.search.cancel?.()
  }

  initSortable() {
    if (!this.hasSelectedTarget) return

    new Sortable(this.selectedTarget, {
      animation: 150,
      handle: '.drag-handle',
      onEnd: () => {
        this.updateHiddenInput()
        this.saveOrder()
      }
    })
  }

  search = lodash.debounce((event) => {
    const query = event.target.value.trim()

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.fetchResults(query)
  }, 300)

  async fetchResults(query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          this.resultsTarget.innerHTML = '<div class="p-3 text-yellow-600 dark:text-yellow-400">Please sign in to search playlists.</div>'
        } else if (response.status === 403) {
          this.resultsTarget.innerHTML = '<div class="p-3 text-red-500">You don\'t have permission to search playlists.</div>'
        } else {
          this.resultsTarget.innerHTML = `<div class="p-3 text-red-500">Search failed (HTTP ${response.status}). Please try again.</div>`
        }
        this.showResults()
        return
      }
      
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text()
        console.error('Non-JSON response:', text)
        this.resultsTarget.innerHTML = '<div class="p-3 text-red-500">Invalid response from server. Please try again.</div>'
        this.showResults()
        return
      }
      
      const playlists = await response.json()
      this.renderResults(playlists)
    } catch (error) {
      console.error('Search failed:', error)
      this.resultsTarget.innerHTML = '<div class="p-3 text-red-500">Search failed. Please try again.</div>'
      this.showResults()
    }
  }

  renderResults(playlists) {
    if (playlists.length === 0) {
      this.resultsTarget.innerHTML = '<div class="p-3 text-gray-500 dark:text-gray-400">No playlists found</div>'
    } else {
      this.resultsTarget.innerHTML = playlists.map(p => this.resultItemTemplate(p)).join('')
    }
    this.showResults()
  }

  resultItemTemplate(playlist) {
    const isSelected = this.isPlaylistSelected(playlist.id)
    const imageHtml = playlist.image_url
      ? `<img src="${playlist.image_url}" class="w-10 h-10 rounded object-cover flex-shrink-0" alt="">`
      : '<div class="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>'

    return `
      <button type="button"
              class="w-full p-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 ${isSelected ? 'opacity-50 cursor-not-allowed' : ''}"
              data-action="click->playlist-picker#addPlaylist"
              data-playlist-id="${playlist.id}"
              data-playlist-name="${this.escapeHtml(playlist.name)}"
              data-playlist-user="${this.escapeHtml(playlist.user_name)}"
              data-playlist-user-type="${playlist.user_type}"
              data-playlist-image="${playlist.image_url || ''}"
              ${isSelected ? 'disabled' : ''}>
        ${imageHtml}
        <div class="flex-1 min-w-0">
          <div class="font-medium truncate text-gray-900 dark:text-gray-100">${this.escapeHtml(playlist.name)}</div>
          <div class="text-sm text-gray-500 dark:text-gray-400 truncate">${this.escapeHtml(playlist.user_name)} (${playlist.user_type})</div>
        </div>
        ${isSelected
          ? '<span class="text-xs text-gray-400 flex-shrink-0">Added</span>'
          : '<span class="material-symbols-outlined text-gray-400 flex-shrink-0">add</span>'}
      </button>
    `
  }

  addPlaylist(event) {
    const btn = event.currentTarget
    if (btn.disabled) return

    // Check max limit
    const currentCount = this.selectedTarget.querySelectorAll('[data-playlist-id]').length
    if (currentCount >= this.maxPlaylistsValue) {
      alert(`Maximum ${this.maxPlaylistsValue} playlists allowed`)
      return
    }

    const playlist = {
      id: btn.dataset.playlistId,
      name: btn.dataset.playlistName,
      user: btn.dataset.playlistUser,
      userType: btn.dataset.playlistUserType,
      image: btn.dataset.playlistImage
    }

    this.appendPlaylistItem(playlist)
    this.updateHiddenInput()
    this.updateEmptyState()
    this.updateCounter()
    this.hideResults()
    this.searchTarget.value = ''
  }

  appendPlaylistItem(playlist) {
    const imageHtml = playlist.image
      ? `<img src="${playlist.image}" class="w-10 h-10 rounded object-cover flex-shrink-0" alt="">`
      : '<div class="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>'

    const html = `
      <div class="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 group"
           data-playlist-id="${playlist.id}">
        <span class="drag-handle cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0">
          <span class="material-symbols-outlined">drag_indicator</span>
        </span>
        ${imageHtml}
        <div class="flex-1 min-w-0">
          <div class="font-medium truncate text-gray-900 dark:text-gray-100">${this.escapeHtml(playlist.name)}</div>
          <div class="text-sm text-gray-500 dark:text-gray-400 truncate">${this.escapeHtml(playlist.user)} (${playlist.userType})</div>
        </div>
        <button type="button"
                class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                data-action="click->playlist-picker#removePlaylist"
                title="Remove playlist">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    `
    this.selectedTarget.insertAdjacentHTML('beforeend', html)
  }

  removePlaylist(event) {
    event.preventDefault()
    const item = event.currentTarget.closest('[data-playlist-id]')
    item.remove()
    this.updateHiddenInput()
    this.updateEmptyState()
    this.updateCounter()
  }

  updateHiddenInput() {
    const ids = Array.from(this.selectedTarget.querySelectorAll('[data-playlist-id]'))
      .map(el => el.dataset.playlistId)
    this.inputTarget.value = ids.join(',')
  }

  isPlaylistSelected(id) {
    return this.selectedTarget.querySelector(`[data-playlist-id="${id}"]`) !== null
  }

  showResults() {
    this.resultsTarget.classList.remove('hidden')
  }

  hideResults() {
    this.resultsTarget.classList.add('hidden')
  }

  // Hide results when clicking outside
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  updateEmptyState() {
    const hasPlaylists = this.selectedTarget.querySelectorAll('[data-playlist-id]').length > 0
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle('hidden', hasPlaylists)
    }
  }

  updateCounter() {
    if (this.hasCounterTarget) {
      const count = this.selectedTarget.querySelectorAll('[data-playlist-id]').length
      this.counterTarget.textContent = `${count}/${this.maxPlaylistsValue}`
    }
  }

  async saveOrder() {
    if (!this.sortUrlValue) return

    const ids = Array.from(this.selectedTarget.querySelectorAll('[data-playlist-id]'))
      .map(el => `playlist[]=${el.dataset.playlistId}`)
      .join('&')

    try {
      await fetch(this.sortUrlValue, {
        method: 'PUT',
        body: ids,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
    } catch (error) {
      console.error('Failed to save order:', error)
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
